import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { AppDispatch, RootState } from "@/store"
import {
    fetchLiveWorkflows,
    selectLiveWorkflows,
} from "../../store/liveWorkflowSlice"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import { SearchBar } from "@/modules/shared/ui/components/custom/SearchBar"
import {
    DataTable,
    type TableColumn,
    type Action as TableAction,
} from "@/modules/shared/ui/components/custom/CustomTable"

type WorkflowSummary = {
    id: string
    workflowId: string
    workflowName: string
    description?: string
    liveCount: number
    completedCount: number
    updatedAt?: string
    batchCount: number
}

const LIVE_STATUSES = new Set(["NotStarted", "InProgress", "Paused"])

export default function LiveWorkflowsPage() {
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { isLoading, error } = useSelector(
        (state: RootState) => state.liveWorkflows,
    )
    const liveInstances = useSelector(selectLiveWorkflows)

    const [search, setSearch] = useState("")

    const handleViewDetails = (workflowId: string) => {
        navigate(`/onboarding/workflows/live/${workflowId}`)
    }

    useEffect(() => {
        dispatch(fetchLiveWorkflows(undefined))
    }, [dispatch])

    const summaries = useMemo<WorkflowSummary[]>(() => {
        const map = new Map<string, WorkflowSummary>()
        const batchMap = new Map<string, Set<string>>()
        const term = search.trim().toLowerCase()

        for (const instance of liveInstances ?? []) {
            const summary = map.get(instance.workflowId) ?? {
                id: instance.workflowId,
                workflowId: instance.workflowId,
                workflowName: instance.workflowName,
                description: instance.workflowDescription,
                liveCount: 0,
                completedCount: 0,
                updatedAt: instance.updatedAt,
                batchCount: 0,
            }

            if (LIVE_STATUSES.has(instance.status)) {
                summary.liveCount += 1
            }

            if (instance.status === "Completed") {
                summary.completedCount += 1
            }

            // Track the most recent update
            if (
                instance.updatedAt &&
                (!summary.updatedAt || instance.updatedAt > summary.updatedAt)
            ) {
                summary.updatedAt = instance.updatedAt
            }

            if (instance.batchId) {
                const batches = batchMap.get(instance.workflowId) ?? new Set<string>()
                batches.add(instance.batchId)
                batchMap.set(instance.workflowId, batches)
                summary.batchCount = batches.size
            }

            map.set(instance.workflowId, summary)
        }

        const result = Array.from(map.values())

        if (!term) {
            return result
        }

        return result.filter((summary) =>
            summary.workflowName.toLowerCase().includes(term),
        )
    }, [liveInstances, search])

    const columns: TableColumn<WorkflowSummary>[] = [
        {
            key: "workflowName",
            label: "Name",
            render: (wf) => (
                <div>
                    <p className="font-medium text-foreground">{wf.workflowName}</p>
                    {wf.description && (
                        <p className="text-sm text-muted-foreground line-clamp-1">
                            {wf.description}
                        </p>
                    )}
                </div>
            ),
        },
        {
            key: "liveCount",
            label: "Live",
            render: (wf) => (
                <span className="text-green-700 font-semibold">{wf.liveCount}</span>
            ),
        },
        {
            key: "completedCount",
            label: "Completed",
            render: (wf) => (
                <span className="text-foreground">{wf.completedCount}</span>
            ),
        },
        {
            key: "batchCount",
            label: "Batches",
            render: (wf) =>
                wf.batchCount > 0 ? (
                    <span className="text-primary">{wf.batchCount}</span>
                ) : (
                    <span className="text-muted-foreground">-</span>
                ),
        },
        {
            key: "updatedAt",
            label: "Updated",
            render: (wf) =>
                wf.updatedAt ? new Date(wf.updatedAt).toLocaleDateString() : "-",
        },
    ]

    const actions: TableAction<WorkflowSummary>[] = [
        {
            label: "View Details",
            onClick: (wf) => handleViewDetails(wf.workflowId),
        },
    ]

    return (
        <div className="p-4 space-y-6">
            <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-foreground">Live Workflows</h1>
                        <p className="text-sm text-muted-foreground">
                            Monitor and manage active workflow instances across your
                            organisation.
                        </p>
                    </div>
                    <Button
                        onClick={() => dispatch(fetchLiveWorkflows(undefined))}
                        disabled={isLoading}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        Refresh
                    </Button>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                    <SearchBar
                        placeholder="Search by workflow name"
                        value={search}
                        onChange={setSearch}
                    />
                    <Badge variant="default" className="px-4 py-2 text-sm">
                        {summaries.length} Workflows
                    </Badge>
                    {error && (
                        <div className="text-sm text-destructive flex items-center gap-2">
                            <span>{error}</span>
                            <button
                                type="button"
                                className="underline"
                                onClick={() => dispatch(fetchLiveWorkflows(undefined))}
                            >
                                retry
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={summaries}
                actions={actions}
                emptyMessage={
                    isLoading ? "Loading live workflows..." : "No live workflows found"
                }
            />
        </div>
    )
}
